import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { NON_OVERRIDABLE_SELECTED_QUESTION_TOOLTIP } from './utils';
import { ConfigurationType, QuestionConfigurationModalProps } from './types';
import { getInputOverridableSelectedQuestions } from '@features/survey-question-list/utils';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { checkIsValueListMetric } from '@utils/universalTracker';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { useAppSelector } from '@reducers/index';
import { getRootConfig } from '@selectors/globalData';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';

type QuestionConfigurationProps = Pick<QuestionConfigurationModalProps, 'selectedQuestions'> & {
  hiddenOptions?: ConfigurationType[];
  changeConfigType: (type: ConfigurationType) => void;
  dropdownToggle?: JSX.Element;
  disabled?: boolean;
  initiativeId: string;
};

export const QuestionConfigurationDropdown = ({
  selectedQuestions,
  hiddenOptions = [],
  dropdownToggle = <i className='fas fa-bars' />,
  changeConfigType,
  disabled = false,
  initiativeId,
}: QuestionConfigurationProps) => {
  const history = useHistory();
  const inputOverridableQuestions = getInputOverridableSelectedQuestions(selectedQuestions);
  const invalidInputOverrides = !inputOverridableQuestions.length;
  const firstQuestion: BulkActionUtr | undefined = selectedQuestions[0];
  const isValueListMetric =
    firstQuestion &&
    firstQuestion.valueType &&
    checkIsValueListMetric({
      valueType: firstQuestion.valueType,
      valueValidation: firstQuestion.valueValidation,
    });

  const rootConfig = useAppSelector(getRootConfig);
  const canAccessAggregationOverrides = FeaturePermissions.canAccessAggregationOverrides(rootConfig);

  return (
    <UncontrolledDropdown>
      <DropdownToggle disabled={disabled} color='secondary' outline className='px-2'>
        {dropdownToggle}
      </DropdownToggle>
      <DropdownMenu>
        {hiddenOptions.includes(ConfigurationType.MetricOverrides) ? null : (
          <DropdownItem onClick={() => changeConfigType(ConfigurationType.MetricOverrides)}>
            <i className='fa-light fa-square-question fs-5 me-2'></i> {ConfigurationType.MetricOverrides}
          </DropdownItem>
        )}
        {hiddenOptions.includes(ConfigurationType.InputOverrides) ? null : (
          <SimpleTooltip text={invalidInputOverrides ? NON_OVERRIDABLE_SELECTED_QUESTION_TOOLTIP : undefined}>
            <DropdownItem
              disabled={invalidInputOverrides}
              onClick={() => changeConfigType(ConfigurationType.InputOverrides)}
            >
              <i className='fa-light fa-input-numeric me-2'></i> {ConfigurationType.InputOverrides}
            </DropdownItem>
          </SimpleTooltip>
        )}
        {!canAccessAggregationOverrides || hiddenOptions.includes(ConfigurationType.AggregationOverrides) || selectedQuestions.length > 1 ? null : (
          <DropdownItem onClick={() => changeConfigType(ConfigurationType.AggregationOverrides)}>
            <i className='fa-light fa-input-numeric me-2'></i> {ConfigurationType.AggregationOverrides}{' '}
            <FeatureStability stability={'beta'} />
          </DropdownItem>
        )}
        {hiddenOptions.includes(ConfigurationType.Tags) ? null : (
          <DropdownItem onClick={() => changeConfigType(ConfigurationType.Tags)}>
            <i className='fa-light fa-tags fs-5 me-2'></i> {ConfigurationType.Tags}
          </DropdownItem>
        )}
        {hiddenOptions.includes(ConfigurationType.ValueList) || selectedQuestions.length > 1 ? null : (
          <SimpleTooltip text={isValueListMetric ? undefined : 'No dropdown inputs available for this metric'}>
            <DropdownItem
              onClick={() =>
                history.push(
                  `${generateUrl(ROUTES.ADMIN_DASHBOARD, { initiativeId })}/questions/${firstQuestion?._id}/dropdown-options`,
                )
              }
              disabled={!isValueListMetric}
            >
              <i className='fa-light fa-ballot-check fs-5 me-2'></i> {ConfigurationType.ValueList}{' '}
              <FeatureStability stability={'beta'} />
            </DropdownItem>
          </SimpleTooltip>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};
